import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function ReservationPanel({ equipmentId, currentUser }) {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const [list, setList] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ start_date: today, end_date: today, quantity: 1, purpose: "", event_id: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reservations", { params: { equipment_id: equipmentId } }).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/events").then((r) => setEvents(r.data.filter((e) => e.status === "upcoming" || e.status === "ongoing"))).catch(() => {}); /* eslint-disable-next-line */ }, [equipmentId]);

  const pickEvent = (v) => {
    const ev = events.find((e) => e.id === v);
    if (!ev) { setForm({ ...form, event_id: "" }); return; }
    setForm({ ...form, event_id: v, start_date: ev.start_date < today ? today : ev.start_date, end_date: ev.end_date, purpose: form.purpose || ev.name });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reservations", { equipment_id: equipmentId, ...form, event_id: form.event_id || null });
      toast.success(t("reserved_ok"));
      setForm({ ...form, purpose: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const cancel = async (id) => { await api.delete(`/reservations/${id}`); load(); };

  return (
    <div className="glass rounded-xl p-4 space-y-3" data-testid="reservation-panel">
      <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5" />{t("reservations")}</div>
      {events.length > 0 && (
        <select data-testid="reservation-event-select" value={form.event_id} onChange={(e) => pickEvent(e.target.value)} className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-sm px-2 text-slate-200">
          <option value="" className="bg-slate-900">{t("no_event")}</option>
          {events.map((e) => <option key={e.id} value={e.id} className="bg-slate-900">{e.name} · {e.start_date}</option>)}
        </select>
      )}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs text-slate-400">{t("start_date")}</Label>
          <Input data-testid="reservation-start-input" type="date" min={today} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("end_date")}</Label>
          <Input data-testid="reservation-end-input" type="date" min={form.start_date} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("quantity")}</Label>
          <Input data-testid="reservation-qty-input" type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "1", 10) })} className="bg-white/5 border-white/10 mt-1" />
        </div>
      </div>
      <div className="flex gap-2">
        <Input data-testid="reservation-purpose-input" placeholder={t("purpose")} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="bg-white/5 border-white/10" />
        <Button data-testid="reservation-submit-btn" onClick={submit} disabled={busy} className="bg-indigo-500 hover:bg-indigo-600 whitespace-nowrap">{busy ? "…" : t("reserve")}</Button>
      </div>
      <div className="space-y-1.5">
        {list.length === 0 && <div className="text-xs text-slate-500">{t("no_reservations")}</div>}
        {list.map((r) => {
          const active = r.start_date <= today && r.end_date >= today;
          return (
            <div key={r.id} data-testid={`reservation-item-${r.id}`} className={`flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg border ${active ? "border-indigo-400/40 bg-indigo-500/10" : "border-white/10 bg-white/5"}`}>
              <span><span className="font-medium">{r.user_name}</span> · ×{r.quantity} · {r.start_date} → {r.end_date}{r.purpose ? ` · ${r.purpose}` : ""}</span>
              {(currentUser?.role === "admin" || currentUser?.id === r.user_id) && (
                <button data-testid={`reservation-cancel-${r.id}`} onClick={() => cancel(r.id)} className="text-slate-400 hover:text-rose-300 ml-2" title={t("cancel_reservation")}><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
